import { z } from "zod";
/**
 * Ideation kind taxonomies — per-tenant, editable vocabularies for *map* kinds
 * and *node* kinds. One reusable "kind definition" shape; map kinds carry a
 * system-controlled `behavior` (the renderer keys layout off it), node kinds do
 * not. `color`/`icon` are constrained enums but degrade gracefully via `.catch()`
 * so a drifted stored row never breaks the whole taxonomy (loose-response rule).
 *
 * Supersedes the closed `MAP_KINDS` enum: the closed enum moves to `behavior`;
 * `kind` becomes a loose string resolved against the taxonomy. ADR-0066.
 */
// ─── Behaviour (the closed, system-controlled enum) ────────────────────────
export const MAP_BEHAVIORS = ["hub", "graph", "flow", "freestyle"];
export const MapBehaviorSchema = z.enum(MAP_BEHAVIORS);
// ─── Curated palette + icon allow-lists (keys only; web owns the classes) ──
export const KIND_COLORS = [
    "brand",
    "info",
    "warning",
    "success",
    "danger",
    "neutral",
    "accent",
];
export const KindColorSchema = z.enum(KIND_COLORS);
export const KIND_ICONS = [
    "cpu",
    "boxes",
    "file-text",
    "scroll-text",
    "scale",
    "workflow",
    "lightbulb",
    "help-circle",
    "badge-check",
    "users",
    "box",
    "user",
    "circle",
    "network",
];
export const KindIconSchema = z.enum(KIND_ICONS);
// ─── The reusable kind definition ──────────────────────────────────────────
// `color`/`icon`/`behavior` are stored as LOOSE strings (not the enums above),
// so one drifted row never fails the whole taxonomy parse. The known values are
// `KIND_COLORS`/`KIND_ICONS`/`MAP_BEHAVIORS`; the UI keys colour/icon/layout off
// them and falls back (neutral / circle / freestyle) for anything else — the
// same "loose response, narrow at render" posture used across the codebase.
// (Loose strings also keep these schemas representable in the generated OpenAPI,
// which a `z.enum().catch()` is not.)
export const KindDefBaseSchema = z.object({
    slug: z.string().min(1).max(60),
    label: z.string().min(1).max(60),
    color: z.string().min(1).max(40),
    icon: z.string().min(1).max(40),
    builtin: z.boolean().default(false),
});
export const MapKindDefSchema = KindDefBaseSchema.extend({
    behavior: z.string().min(1).max(20),
});
const NodeKindDefSchema = KindDefBaseSchema;
export const MapTaxonomySchema = z.object({ kinds: z.array(MapKindDefSchema) });
export const NodeTaxonomySchema = z.object({ kinds: z.array(NodeKindDefSchema) });
// ─── Defaults (per-tenant seed; the service returns these when no row exists) ─
export const DEFAULT_MAP_TAXONOMY = {
    kinds: [
        {
            slug: "hub",
            label: "Hub",
            color: "neutral",
            icon: "network",
            behavior: "hub",
            builtin: true,
        },
        {
            slug: "freestyle",
            label: "Freestyle",
            color: "neutral",
            icon: "circle",
            behavior: "freestyle",
            builtin: true,
        },
        {
            slug: "graph",
            label: "Graph",
            color: "brand",
            icon: "workflow",
            behavior: "graph",
            builtin: false,
        },
        {
            slug: "flow",
            label: "Flow",
            color: "info",
            icon: "workflow",
            behavior: "flow",
            builtin: false,
        },
    ],
};
export const DEFAULT_NODE_TAXONOMY = {
    kinds: [
        { slug: "device", label: "Device", color: "success", icon: "cpu", builtin: false },
        { slug: "component", label: "Component", color: "success", icon: "boxes", builtin: false },
        { slug: "brief", label: "Brief", color: "brand", icon: "file-text", builtin: false },
        { slug: "patent", label: "Patent", color: "warning", icon: "scroll-text", builtin: false },
        { slug: "claim", label: "Claim", color: "warning", icon: "scale", builtin: false },
        { slug: "methodology", label: "Methodology", color: "info", icon: "workflow", builtin: false },
        { slug: "concept", label: "Concept", color: "info", icon: "lightbulb", builtin: false },
        {
            slug: "open-question",
            label: "Open question",
            color: "danger",
            icon: "help-circle",
            builtin: false,
        },
        { slug: "standard", label: "Standard", color: "success", icon: "badge-check", builtin: false },
        { slug: "party", label: "Party", color: "accent", icon: "users", builtin: false },
        { slug: "insight", label: "Insight", color: "accent", icon: "lightbulb", builtin: false },
    ],
};
// ─── Pure resolution helpers (used by web + api) ───────────────────────────
export function resolveKindDef(kinds, slug) {
    return kinds.find((k) => k.slug === slug) ?? null;
}
export function resolveMapBehavior(taxonomy, slug) {
    const behavior = resolveKindDef(taxonomy.kinds, slug)?.behavior;
    // Loose stored string → narrow to a known behaviour; unknown → freestyle.
    return MAP_BEHAVIORS.includes(behavior ?? "")
        ? behavior
        : "freestyle";
}
// ─── Update DTO (request: tight) + Response (loose, per loose-response rule) ─
function slugsUnique(kinds, ctx) {
    const seen = new Set();
    kinds.forEach((k, i) => {
        const key = k.slug.toLowerCase();
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["kinds", i, "slug"],
                message: "Slugs must be unique",
            });
        }
        seen.add(key);
    });
}
export const MapTaxonomyUpdateSchema = MapTaxonomySchema.superRefine((tax, ctx) => {
    slugsUnique(tax.kinds, ctx);
    for (const slug of ["hub", "freestyle"]) {
        if (!tax.kinds.some((k) => k.slug === slug && k.builtin)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["kinds"],
                message: `The builtin "${slug}" kind cannot be removed`,
            });
        }
    }
});
export const NodeTaxonomyUpdateSchema = NodeTaxonomySchema.superRefine((tax, ctx) => slugsUnique(tax.kinds, ctx));
export const MapTaxonomyResponseSchema = z.object({
    taxonomy: MapTaxonomySchema,
    updatedBy: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const NodeTaxonomyResponseSchema = z.object({
    taxonomy: NodeTaxonomySchema,
    updatedBy: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
//# sourceMappingURL=ideation-taxonomy.js.map